<template>
  <div class="flex gap-2">
    <!-- Loading state -->
    <div v-if="loading" class="flex gap-2">
      <div
        v-for="n in 3"
        :key="n"
        class="size-18 animate-pulse rounded-lg bg-gray-200"
      />
    </div>

    <!-- Images -->
    <div
      v-for="(imageUrl, index) in displayImages"
      v-else
      :key="index"
      class="js-pattern-preview-container relative"
      :style="getContainerStyle(index)"
    >
      <img
        ref="imageElements"
        :src="imageUrl"
        alt="Pattern product preview"
        class="size-18 rounded-lg border border-gray-100 bg-gray-100 object-cover"
        :style="getImageStyle()"
        loading="lazy"
      />

      <!-- Overlay for "+X more" count -->
      <span
        v-if="shouldShowOverlay(index)"
        class="js-pattern-preview-overlay absolute left-0 top-0 flex size-18 items-center justify-center rounded-lg bg-black/50 text-xl font-bold text-white"
        :style="overlayStyle"
      >
        +{{ overlayCount }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useWindowSize } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import { patternProductPreviewQuery } from '@/shared/composables/patternProducts/patternProductsStorefront.queries';
import { usePatternProductsStore } from '@/shared/pinia/modules/pattern-products';
import { getCountryCode } from '@/shared/utils/country';
import { isSmallMobileView } from '@/shared/utils/dom';
import { debounce } from '@/shared/utils/functions';

const MAX_DISPLAYABLE_IMAGES = 5;
const ANIMATION_DELAY = 50;
const STAGGER_DELAY = 100;

const patternProductsStore = usePatternProductsStore();

const imageElements = ref<HTMLImageElement[]>([]);

const queryVariables = computed(() => {
  const variantIds = patternProductsStore.activePatternProducts
    .map((pp) => pp.variant_id)
    .filter((id): id is string => !!id);

  return {
    ids: variantIds,
    country: getCountryCode()
  };
});

const { loading, result } = useQuery(
  patternProductPreviewQuery,
  queryVariables,
  {
    clientId: 'storefront'
  }
);

const imageUrls = computed(() => {
  if (!result.value?.nodes) return [];

  type ProductVariantNode = {
    __typename?: string;
    image?: {
      url?: string;
    };
  };

  return (result.value.nodes as ProductVariantNode[])
    .filter(
      (node: ProductVariantNode) => node && node.__typename === 'ProductVariant'
    )
    .map((node: ProductVariantNode) => node?.image?.url)
    .filter((url): url is string => !!url);
});

const { width } = useWindowSize();

const totalAvailableProducts = computed(() => imageUrls.value.length);
const desiredMaxVisible = computed(() =>
  isSmallMobileView(width.value) ? 3 : 5
);
const maxVisibleImages = computed(() =>
  Math.min(desiredMaxVisible.value, totalAvailableProducts.value)
);
const willHaveOverlay = computed(
  () => totalAvailableProducts.value > maxVisibleImages.value
);
const overlayImageIndex = computed(() =>
  willHaveOverlay.value ? maxVisibleImages.value - 1 : -1
);
const overlayCount = computed(() =>
  willHaveOverlay.value
    ? totalAvailableProducts.value - maxVisibleImages.value + 1
    : 0
);

// Images to display (limited to what's actually visible)
const displayImages = computed(() => {
  return imageUrls.value.slice(0, MAX_DISPLAYABLE_IMAGES);
});

// Style getters
const getContainerStyle = (index: number) => ({
  display: index < maxVisibleImages.value ? '' : 'none'
});

const getImageStyle = () => ({
  opacity: '0',
  transition: 'opacity 0.3s ease-in-out'
});

const shouldShowOverlay = (index: number) => {
  return index === overlayImageIndex.value && overlayCount.value > 0;
};

const overlayStyle = computed(() => ({
  display: 'flex',
  transition: 'none'
}));

const handleResize = debounce(() => {
  animateImages();
}, 100);

const animateImages = () => {
  // Small delay to ensure DOM is updated
  setTimeout(() => {
    displayImages.value.forEach((imageUrl, index) => {
      if (imageUrl && !shouldShowOverlay(index)) {
        setTimeout(() => {
          const imgElement = imageElements.value[index];
          if (imgElement) {
            imgElement.style.opacity = '1';
          }
        }, index * STAGGER_DELAY);
      }
    });
  }, ANIMATION_DELAY);
};

watch(
  () => imageUrls.value,
  (newImageUrls) => {
    if (newImageUrls.length > 0) {
      animateImages();
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>
